import { useState, useEffect } from 'react';

/**
 * The dashboard / student-surface palette, imported by 48 files.
 *
 * Every value is a CSS variable declared for BOTH themes in
 * styles/index.css (see the "Dashboard / student-surface palette" block).
 * Keeping literals out of here is what lets this whole surface follow
 * <html data-theme> — while these were baked-in hex, the app rail could go
 * dark while the page it framed stayed white.
 *
 * Tokens that mean the same thing as the main AppRail palette point at the
 * shared --fb-* var rather than redefining it, so the two can't drift.
 * The var() fallbacks are the original light values.
 */
export const C = {
  bg:         'var(--fbd-bg, #f7f7f5)',            // warm off-white canvas
  bg2:        'var(--fbd-bg2, #f1f3f6)',
  surface:    'var(--fbd-surface, #ffffff)',
  ink:        'var(--fbd-ink, #0f172a)',
  ink2:       'var(--fbd-ink2, #334155)',
  ink3:       'var(--fbd-ink3, #475569)',
  mut:        'var(--fbd-mut, #64748b)',           // secondary body text
  ink4:       'var(--fbd-ink4, #94a3b8)',
  line:       'var(--fbd-line, #ececec)',           // hairline border
  line2:      'var(--fbd-line2, #f1f3f6)',
  track:      'var(--fbd-track, #eef0f3)',         // progress-bar track
  fill:       'var(--fbd-fill, #f8fafc)',          // inset field / ghost button bg
  accent:     'var(--fbd-accent, #0084f8)',        // brand blue (robot visor)
  accentBar:  'var(--fbd-accent, #0084f8)',        // bars & fills
  accentSoft: 'var(--fbd-accent-soft, #e8f3ff)',   // brand blue tint
  accentInk:  'var(--fbd-accent-ink, #08407a)',    // deep blue in light, LIGHT blue in dark
  warn:       'var(--fb-warn-ink, #b45309)',
  warnSoft:   'var(--fb-warn-soft, #fffbeb)',
  ok:         'var(--fb-success-ink, #047857)',
  shadow:     'var(--fbd-shadow, 0 1px 2px rgba(0,0,0,0.06))',
  shadowSm:   'var(--fbd-shadow-sm, 0 1px 2px rgba(0,0,0,0.04))',
  /** Inverted-emphasis button (the "black button"). ALWAYS pair the two —
   *  using `ink` as a fill with white text breaks in dark, where ink is
   *  near-white. Shared with the AppRail palette so both surfaces match. */
  invert:     'var(--fb-invert-bg, oklch(0.20 0.012 270))',
  invertInk:  'var(--fb-invert-ink, #ffffff)',

  // Semantic parity with the AppRail palette so the same sweep and the same
  // review rules apply to both surfaces. Each status is a triple —
  // `X` text, `XSoft` chip fill, `XLine` border — plus `XFill`, a mid-dark
  // saturated fill for buttons that carry `onAccent` text.
  danger:      'var(--fb-danger-ink, oklch(0.50 0.18 25))',
  dangerSoft:  'var(--fb-danger-soft, oklch(0.96 0.04 25))',
  dangerLine:  'var(--fb-danger-line, oklch(0.85 0.06 25))',
  dangerFill:  'var(--fb-danger-fill, oklch(0.55 0.20 25))',
  success:     'var(--fb-success-ink, oklch(0.45 0.16 145))',
  successSoft: 'var(--fb-success-soft, oklch(0.95 0.06 145))',
  successLine: 'var(--fb-success-line, oklch(0.85 0.08 145))',
  successFill: 'var(--fb-success-fill, oklch(0.50 0.15 155))',
  warnLine:    'var(--fb-warn-line, oklch(0.87 0.07 60))',
  warnFill:    'var(--fb-warn-fill, oklch(0.55 0.15 60))',
  info:        'var(--fb-info-ink, oklch(0.50 0.16 250))',
  infoSoft:    'var(--fb-info-soft, oklch(0.96 0.03 250))',
  infoLine:    'var(--fb-info-line, oklch(0.86 0.06 250))',
  infoFill:    'var(--fb-info-fill, oklch(0.52 0.16 250))',
  /** Foreground for text sitting ON an accent/status fill. */
  onAccent:    'var(--fb-on-accent, #ffffff)',
};

// Per-domain hue tokens — matches the Refined Classic design system.
/**
 * Per-domain hue tokens — the Refined Classic categorical set.
 *
 * `bg` and `text` swap roles between themes (pale-fill/dark-text in light,
 * deep-fill/light-text in dark). `solid` deliberately stays mid-tone in both:
 * it fills dots, bars and chart series, which read against either canvas and
 * would lose their identity if they shifted per theme.
 */
export const HUE = {
  // "brand" — the FresherBot blue; plans, heatmap & primary accents use it.
  brand:  { bg: 'var(--hue-brand-bg, #e8f3ff)',  text: 'var(--hue-brand-text, #0a6fd4)',  solid: 'var(--hue-brand-solid, #0084f8)' },
  violet: { bg: 'var(--hue-violet-bg, #f5f3ff)', text: 'var(--hue-violet-text, #6d28d9)', solid: 'var(--hue-violet-solid, #7c3aed)' },
  amber:  { bg: 'var(--hue-amber-bg, #fffbeb)',  text: 'var(--hue-amber-text, #b45309)',  solid: 'var(--hue-amber-solid, #d97706)' },
  green:  { bg: 'var(--hue-green-bg, #ecfdf5)',  text: 'var(--hue-green-text, #047857)',  solid: 'var(--hue-green-solid, #059669)' },
  rose:   { bg: 'var(--hue-rose-bg, #fff1f2)',   text: 'var(--hue-rose-text, #be123c)',   solid: 'var(--hue-rose-solid, #e11d48)' },
  teal:   { bg: 'var(--hue-teal-bg, #f0fdfa)',   text: 'var(--hue-teal-text, #0f766e)',   solid: 'var(--hue-teal-solid, #0d9488)' },
  blue:   { bg: 'var(--hue-blue-bg, #eff6ff)',   text: 'var(--hue-blue-text, #1d4ed8)',   solid: 'var(--hue-blue-solid, #2563eb)' },
  indigo: { bg: 'var(--hue-indigo-bg, #eef2ff)', text: 'var(--hue-indigo-text, #4338ca)', solid: 'var(--hue-indigo-solid, #4f46e5)' },
  slate:  { bg: 'var(--hue-slate-bg, #f8fafc)',  text: 'var(--hue-slate-text, #334155)',  solid: 'var(--hue-slate-solid, #475569)' },
};

export const MONO = '"JetBrains Mono", "Fira Code", ui-monospace, monospace';

export const greeting = (): string => {
  const h = new Date().getHours();
  if (h < 5) return 'Up late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

export function useCountUp(target: number, duration = 1100, delay = 300): number {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf: number | null = null;
    const to = setTimeout(() => {
      const start = performance.now();
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(Math.round(target * eased));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(to); if (raf) cancelAnimationFrame(raf); };
  }, [target, duration, delay]);
  return val;
}
